import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Home, ChevronDown, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppShell } from '@/components/layout/AppShell';
import { useObservation } from '@/lib/state/useObservation';
import { itemsInOrder } from '@/lib/schema/loader';
import providerData from '@/data/providers.json';
import { useAuth } from '@/lib/auth/AuthContext';
import { AGE_BANDS, SPECIAL_NEED_TYPES } from '@/lib/state/types';
import type { EnrollmentByAge } from '@/lib/state/types';

/** One licensed family child care home, from `data-sources/fcc-dece-providers.csv`. */
interface ProviderRow {
  /** OCFS license number — unique per home, so it keys the selection. */
  id: string;
  /** The contracted umbrella organisation — labelled "Network" in the UI. */
  partner: string;
  /** The licensed home/program — labelled "Program" in the UI. */
  program: string;
  /** The person who runs the home — labelled "Provider" in the UI. */
  provider: string;
  address: string;
  borough: string;
}

const rowsAll: ProviderRow[] = providerData as ProviderRow[];

export function MetadataStep() {
  const { observationId } = useParams<{ observationId: string }>();
  const { state, setMetadata } = useObservation();
  const { session } = useAuth();
  const navigate = useNavigate();

  const meta = state.metadata;

  // Force observer identity to the signed-in Google account — eliminates
  // typos and makes the observer→submission match in the dashboard
  // deterministic. Also re-runs after the record hydrates from IndexedDB,
  // which is why we depend on the two observer fields (drafts saved before
  // this feature landed have empty values and would otherwise stay empty).
  useEffect(() => {
    if (!session) return;
    const name = session.name ?? session.email;
    const patch: Partial<typeof meta> = {};
    if (meta.observerEmail !== session.email) patch.observerEmail = session.email;
    if (meta.observerName !== name) patch.observerName = name;
    if (Object.keys(patch).length > 0) setMetadata(patch);
  }, [session, meta.observerEmail, meta.observerName, setMetadata]);

  const next = () => {
    const first = itemsInOrder('FCCERS-3')[0];
    if (first && observationId) {
      navigate(`/observation/${observationId}/items/${first.id}`);
    }
  };

  /**
   * Network / Provider / Partner are three views of the same row, so any one
   * of them can be the starting point: choosing a value in one field narrows
   * the other two and fills in whatever the choice already determines. None of
   * the three is a prerequisite for the others.
   */
  const rowById = useMemo(() => {
    const m = new Map<string, ProviderRow>();
    for (const r of rowsAll) m.set(r.id, r);
    return m;
  }, []);

  const selectedRow = meta.providerLicenseId
    ? rowById.get(meta.providerLicenseId)
    : undefined;

  const partners = useMemo(
    () => [...new Set(rowsAll.map((r) => r.partner))].sort((a, b) => a.localeCompare(b)),
    [],
  );

  // Rows still reachable given whatever has been chosen so far.
  const candidates = useMemo(
    () =>
      rowsAll.filter(
        (r) =>
          (!meta.networkName || r.partner === meta.networkName) &&
          (!meta.providerName || r.program === meta.providerName) &&
          (!meta.providerPerson || r.provider === meta.providerPerson),
      ),
    [meta.networkName, meta.providerName, meta.providerPerson],
  );

  /**
   * Options for one field come from the rows the *other* fields still allow,
   * so a field never hides the value that is currently selected in it.
   */
  const optionsFor = (field: 'program' | 'provider') => {
    const rows = rowsAll.filter(
      (r) =>
        (!meta.networkName || r.partner === meta.networkName) &&
        (field === 'program'
          ? !meta.providerPerson || r.provider === meta.providerPerson
          : !meta.providerName || r.program === meta.providerName),
    );
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r[field], (counts.get(r[field]) ?? 0) + 1);
    return [...counts.keys()]
      .sort((a, b) => a.localeCompare(b))
      .map((v) => {
        // Two rows can share a name; show the borough to tell them apart
        // rather than making the observer guess.
        const dup = (counts.get(v) ?? 0) > 1;
        const row = rows.find((r) => r[field] === v);
        return {
          value: v,
          label: dup && row ? `${v} — ${row.borough}` : v,
        };
      });
  };

  const programOptions = useMemo(() => optionsFor('program'), [meta.networkName, meta.providerPerson]);
  const providerOptions = useMemo(() => optionsFor('provider'), [meta.networkName, meta.providerName]);

  /** Commit whatever the narrowed set now determines unambiguously. */
  const settle = (patch: Partial<typeof meta>) => {
    const next = { ...meta, ...patch };
    const rows = rowsAll.filter(
      (r) =>
        (!next.networkName || r.partner === next.networkName) &&
        (!next.providerName || r.program === next.providerName) &&
        (!next.providerPerson || r.provider === next.providerPerson),
    );
    if (rows.length === 1) {
      const r = rows[0];
      setMetadata({
        ...patch,
        networkName: r.partner,
        providerName: r.program,
        providerPerson: r.provider,
        siteName: r.program,
        siteAddress: r.address,
        providerLicenseId: r.id,
      });
      return;
    }
    // Still ambiguous — keep the choice, drop anything it invalidated.
    setMetadata({
      ...patch,
      siteAddress: '',
      providerLicenseId: '',
      ...(next.providerName && !rows.some((r) => r.program === next.providerName)
        ? { providerName: '', siteName: '' }
        : {}),
      ...(next.providerPerson && !rows.some((r) => r.provider === next.providerPerson)
        ? { providerPerson: '' }
        : {}),
    });
  };

  const handleNetworkChange = (v: string) => settle({ networkName: v });
  const handleProviderChange = (v: string) =>
    settle({ providerName: v, siteName: v });
  const handlePersonChange = (v: string) => settle({ providerPerson: v });

  const enrolledByAgeTotal = AGE_BANDS.reduce(
    (sum, b) => sum + (meta.enrollmentByAge[b.key] || 0),
    0,
  );
  // The scoresheet asks for enrolment twice — once as a total, once split by
  // age band. Surfacing the mismatch here beats discovering it after submit.
  const ageMismatch =
    enrolledByAgeTotal > 0 &&
    meta.childrenEnrolled > 0 &&
    enrolledByAgeTotal !== meta.childrenEnrolled;

  const setAge = (key: keyof EnrollmentByAge, value: number) =>
    setMetadata({ enrollmentByAge: { ...meta.enrollmentByAge, [key]: value } });

  const toggleNeed = (value: string) => {
    const set = new Set(meta.specialNeedTypes);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    setMetadata({
      specialNeedTypes: SPECIAL_NEED_TYPES.filter((t) => set.has(t.value)).map(
        (t) => t.value,
      ),
      ...(set.has('other') ? {} : { specialNeedOther: '' }),
    });
  };

  const canAdvance = meta.observerName.trim() && meta.providerName.trim();

  return (
    <AppShell instrumentId="FCCERS-3" showSidebar={false}>
      <div className="mx-auto w-full max-w-3xl px-6 py-10">
        <h2 className="font-serif text-2xl font-bold text-dece-navy">
          Observation details
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Capture session context before scoring. Autosaved locally as you type.
        </p>

        {/* ── Observer info ── */}
        <SectionHeading>Observer</SectionHeading>
        <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Observer name" required>
            <input
              value={meta.observerName}
              readOnly
              className="input bg-muted/50"
            />
          </Field>
          <Field label="Observer email" required>
            <input
              type="email"
              value={meta.observerEmail}
              readOnly
              className="input bg-muted/50"
            />
          </Field>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Signed in as {session?.email}. Use "Sign out" in the header to switch accounts.
        </p>

        {/* ── FCC Home / Provider ── */}
        <SectionHeading>Network, provider &amp; program</SectionHeading>
        <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Network">
            <SearchableSelect
              value={meta.networkName}
              onChange={handleNetworkChange}
              options={partners.map((p) => ({ value: p, label: p }))}
              placeholder={`Search ${partners.length} networks…`}
            />
          </Field>
          <Field label="Provider">
            <SearchableSelect
              value={meta.providerPerson}
              onChange={handlePersonChange}
              options={providerOptions}
              placeholder={`Search ${providerOptions.length.toLocaleString()} providers…`}
            />
          </Field>
          <Field label="Program" required>
            <SearchableSelect
              value={meta.providerName}
              onChange={handleProviderChange}
              options={programOptions}
              placeholder={`Search ${programOptions.length.toLocaleString()} programs…`}
            />
          </Field>
          <Field label="Site address">
            <input
              value={meta.siteAddress}
              readOnly
              className="input bg-muted/50"
              placeholder={
                candidates.length > 1
                  ? `${candidates.length.toLocaleString()} providers still match`
                  : 'Fills in once a provider is identified'
              }
            />
          </Field>
          <p className="text-xs text-muted-foreground md:col-span-2">
            Search whichever you know — network, provider, or program. Each
            choice narrows the others, and the rest fill in as soon as one
            record matches.
            {selectedRow ? ` OCFS licence ${selectedRow.id}.` : ''}
          </p>
        </div>

        {/* ── Enrolment ── */}
        <SectionHeading>Enrollment</SectionHeading>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Number of providers present">
            <NumberInput
              value={meta.providersPresent}
              onChange={(v) => setMetadata({ providersPresent: v })}
            />
          </Field>
          <Field label="Number of children enrolled in the child care home">
            <NumberInput
              value={meta.childrenEnrolled}
              onChange={(v) => setMetadata({ childrenEnrolled: v })}
            />
          </Field>
        </div>

        <Disclosure
          label="Enrollment by age"
          summary={
            enrolledByAgeTotal > 0
              ? `${enrolledByAgeTotal} child${enrolledByAgeTotal === 1 ? '' : 'ren'} entered`
              : 'Not yet broken down'
          }
          warn={ageMismatch}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
            {AGE_BANDS.map((b) => (
              <Field key={b.key} label={b.label}>
                <NumberInput
                  value={meta.enrollmentByAge[b.key]}
                  onChange={(v) => setAge(b.key, v)}
                />
              </Field>
            ))}
          </div>
          {ageMismatch ? (
            <p role="status" className="mt-3 text-xs text-amber-700">
              The age bands add up to {enrolledByAgeTotal}, but{' '}
              {meta.childrenEnrolled} children are recorded as enrolled. Check
              which figure is right before you start scoring.
            </p>
          ) : null}
        </Disclosure>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          <Field label="Highest number provider allows at one time">
            <NumberInput
              value={meta.highestAllowedAtOneTime}
              onChange={(v) => setMetadata({ highestAllowedAtOneTime: v })}
            />
          </Field>
          <Field label="Highest number of children present during observation">
            <NumberInput
              value={meta.childrenPresent}
              onChange={(v) => setMetadata({ childrenPresent: v })}
            />
          </Field>
          <Field label="Total children enrolled in the past month">
            <NumberInput
              value={meta.totalEnrolledPastMonth}
              onChange={(v) => setMetadata({ totalEnrolledPastMonth: v })}
            />
          </Field>
        </div>

        {/* ── Observation window ── */}
        <SectionHeading>Observation</SectionHeading>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Date of observation">
            <input
              type="date"
              value={meta.observationDate}
              onChange={(e) => setMetadata({ observationDate: e.target.value })}
              className="input"
            />
          </Field>
          <Field label="Time observation began">
            <input
              type="time"
              value={meta.startTime}
              onChange={(e) => setMetadata({ startTime: e.target.value })}
              className="input"
            />
          </Field>
          <Field label="Time observation ended">
            <input
              type="time"
              value={meta.endTime}
              onChange={(e) => setMetadata({ endTime: e.target.value })}
              className="input"
            />
          </Field>
        </div>

        {/* ── Special needs ── */}
        <SectionHeading>Special needs</SectionHeading>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Number of children with identified special needs">
            <NumberInput
              value={meta.childrenSpecialNeeds}
              onChange={(v) => setMetadata({ childrenSpecialNeeds: v })}
            />
          </Field>
        </div>
        <div className="mt-3">
          <span className="text-sm font-medium text-foreground/80">
            Check type(s) of special need
          </span>
          <div
            role="group"
            aria-label="Types of special need"
            className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2"
          >
            {SPECIAL_NEED_TYPES.map((t) => (
              <label
                key={t.value}
                className="inline-flex cursor-pointer items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm"
              >
                <input
                  type="checkbox"
                  checked={meta.specialNeedTypes.includes(t.value)}
                  onChange={() => toggleNeed(t.value)}
                  className="h-4 w-4"
                />
                <span>{t.label}</span>
              </label>
            ))}
          </div>
          {meta.specialNeedTypes.includes('other') ? (
            <input
              value={meta.specialNeedOther}
              onChange={(e) => setMetadata({ specialNeedOther: e.target.value })}
              className="input mt-2"
              placeholder="Describe the other special need"
              aria-label="Other special need"
            />
          ) : null}
        </div>

        {/* ── Space & food ── */}
        <SectionHeading>Gross motor space &amp; food</SectionHeading>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Location of gross motor space — indoors">
            <input
              value={meta.grossMotorIndoors}
              onChange={(e) => setMetadata({ grossMotorIndoors: e.target.value })}
              className="input"
            />
          </Field>
          <Field label="Location of gross motor space — outdoors">
            <input
              value={meta.grossMotorOutdoors}
              onChange={(e) => setMetadata({ grossMotorOutdoors: e.target.value })}
              className="input"
            />
          </Field>
          <Field label="Food preferences">
            <input
              value={meta.foodPreferences}
              onChange={(e) => setMetadata({ foodPreferences: e.target.value })}
              className="input"
            />
          </Field>
          <Field label="Food allergies">
            <input
              value={meta.foodAllergies}
              onChange={(e) => setMetadata({ foodAllergies: e.target.value })}
              className="input"
            />
          </Field>
        </div>

        {/* ── Notes ── */}
        <SectionHeading>Notes</SectionHeading>
        <div className="mt-2">
          <textarea
            rows={3}
            value={meta.notes}
            onChange={(e) => setMetadata({ notes: e.target.value })}
            className="input w-full"
            placeholder="General notes about this observation session…"
          />
        </div>

        {/* ── Actions ── */}
        <div className="mt-8 flex items-center justify-between">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => navigate('/')}
          >
            <Home className="h-4 w-4" />
            Home
          </Button>
          <Button onClick={next} disabled={!canAdvance}>
            Begin observation
          </Button>
        </div>
      </div>
      <style>{`
        .input {
          width: 100%;
          border: 1px solid hsl(var(--input));
          border-radius: calc(var(--radius) - 2px);
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          background: hsl(var(--background));
        }
        .input:focus { outline: 2px solid hsl(var(--ring)); outline-offset: 1px; }
        .input:disabled { opacity: 0.5; cursor: not-allowed; }
        select.input { appearance: auto; }
      `}</style>
    </AppShell>
  );
}

/**
 * Numeric field that keeps an empty box empty while typing. A plain
 * `Number(e.target.value)` turns a cleared field into 0, so the observer has
 * to select the zero before they can type — this keeps the caret usable.
 */
function NumberInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <input
      type="number"
      min={0}
      inputMode="numeric"
      value={value === 0 ? '' : value}
      placeholder="0"
      onChange={(e) => {
        const raw = e.target.value;
        onChange(raw === '' ? 0 : Math.max(0, Number(raw)));
      }}
      className="input"
    />
  );
}

/**
 * Collapsed group for the eight age bands. Eight number boxes inline would
 * dominate the form, so they stay folded until wanted — with the running
 * total (and any mismatch) visible on the closed row.
 */
function Disclosure({
  label,
  summary,
  warn,
  children,
}: {
  label: string;
  summary: string;
  warn?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 rounded-md border bg-background">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm"
      >
        <span className="font-medium text-foreground/80">{label}</span>
        <span className="flex items-center gap-2">
          <span className={warn ? 'text-xs text-amber-700' : 'text-xs text-muted-foreground'}>
            {summary}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      {open ? <div className="border-t px-3 py-3">{children}</div> : null}
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mt-6 border-b pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}

function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-1 text-sm ${className ?? ''}`}>
      <span className="font-medium text-foreground/80">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </span>
      {children}
    </div>
  );
}

/**
 * Searchable dropdown (combobox). Displays a text input that filters a list
 * of options. Selecting an option (click or Enter) commits the value.
 */
function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightIdx(0);
  }, [filtered.length]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlightIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx, open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = useCallback(
    (val: string) => {
      onChange(val);
      setQuery('');
      setOpen(false);
    },
    [onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIdx((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filtered[highlightIdx]) select(filtered[highlightIdx].value);
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
    }
  };

  const displayLabel = value
    ? options.find((o) => o.value === value)?.label ?? value
    : '';

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`input flex items-center gap-1 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'}`}
        onClick={() => {
          if (!disabled) {
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
      >
        <Search className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        {open ? (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlightIdx(0);
            }}
            onKeyDown={handleKeyDown}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
            placeholder={placeholder ?? 'Type to search…'}
            autoComplete="off"
          />
        ) : (
          <span
            className={`min-w-0 flex-1 truncate text-sm ${displayLabel ? 'text-foreground' : 'text-muted-foreground'}`}
          >
            {displayLabel || placeholder || 'Select…'}
          </span>
        )}
        {value && !disabled ? (
          <button
            type="button"
            className="flex-shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
              setQuery('');
              setOpen(false);
            }}
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        )}
      </div>
      {open && !disabled && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-background py-1 shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              No results found
            </li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={opt.value}
                role="option"
                aria-selected={opt.value === value}
                className={`cursor-pointer px-3 py-2 text-sm transition-colors ${
                  i === highlightIdx
                    ? 'bg-accent text-accent-foreground'
                    : opt.value === value
                      ? 'bg-accent/50 font-medium'
                      : 'hover:bg-accent/30'
                }`}
                onMouseEnter={() => setHighlightIdx(i)}
                onMouseDown={(e) => {
                  e.preventDefault(); // keep focus on input
                  select(opt.value);
                }}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
